import { Analysis } from '../../shared/models/analysis.model';
import { File } from '../../shared/models/file.model';

export enum OhifApp {
  OhifReact = 'ohif_react',
  OhifViewerV3 = 'ohif_viewer_v3',
}

interface OhifReactLaunchConfig {
  app?: OhifApp.OhifReact
  acquisitionIds?: string[]
  containerId?: string
  fileId?: string
  fileIds?: string[]
  projectId?: string
  sessionId?: string
  studyInstanceUid?: string
  // only one of these should be set
  niftiFile?: File
  imageFile?: File
  taskId?: string
  metaioFile?: File
  analysis?: Analysis
}

interface OhifViewerV3LaunchConfig {
  app: OhifApp.OhifViewerV3
  acquisitionIds?: string[]
  containerId?: string
  fileId?: string
  fileIds?: string[]
  projectId?: string
  sessionId?: string
  studyInstanceUid?: string
  taskId?: string
}

export type OhifLaunchConfig = OhifReactLaunchConfig | OhifViewerV3LaunchConfig

export function buildUrl(config: OhifLaunchConfig): string {
  switch (config.app) {
    case OhifApp.OhifViewerV3: return buildV3ViewerUrl(config);
    default: return buildV2ViewerUrl(config);
  }
}

function buildV2ViewerUrl(data: OhifReactLaunchConfig): string {
  const ohifViewerUrl = `/ohif-viewer/project/${data.projectId}`;
  if (data.taskId) {
    // V2 Tasks
    return `/ohif-viewer/?taskId=${data.taskId}`;
  } else if (data.niftiFile && data.containerId) {
    // NIfTI
    const fileLocation = getAnalysisFileLocation(data);
    return `${ohifViewerUrl}/nifti/${data.containerId}/${encodeURIComponent(data.niftiFile.name)}${fileLocation}`;
  } else if (data.imageFile && data.containerId) {
    // PNG/JPEG
    return `${ohifViewerUrl}/image/${data.containerId}/${encodeURIComponent(data.imageFile.name)}`;
  } else if (data.metaioFile && data.containerId) {
    // MetaIO Header
    return `${ohifViewerUrl}/metaio/${data.containerId}/${encodeURIComponent(data.metaioFile.name)}`;
  } else {
    // DICOM
    return getWebDicomUrl(data, ohifViewerUrl);
  }
}

/**
 * to select the analysis path, if 'inputs' and 'files' location contain same file name.
 */
function getAnalysisFileLocation(data: OhifReactLaunchConfig): string {
  if (data.analysis) {
    const fileId: string = data.niftiFile.file_id;
    let file: File = data.analysis.files.find((x: File) => x.file_id === fileId);
    if (file) {
      return `/?type=files&analysis=${data.analysis._id}`;
    } else {
      file = data.analysis.inputs.find((x: File) => x.file_id === fileId);
      if (file) {
        return `/?type=inputs&analysis=${data.analysis._id}`;
      }
    }
  }
  return '';
}

function buildV3ViewerUrl(config: OhifViewerV3LaunchConfig): string {
  const baseURL = `/ohif-viewer-v3/project/${config.projectId}`;
  if (config.fileId) {
    return `${baseURL}/viewer?fileId=${config.fileId}`;
  } else if (config.sessionId) {
    return `${baseURL}/viewer?sessionId=${config.sessionId}`;
  } else if (config.taskId) {
    return `${baseURL}/reader?taskId=${config.taskId}`;
  }
  return baseURL;
}

/**
 * Interpolate the launch data into a URL for the OHIF Viewer
 * @param data The launch config
 * @param baseurl The base URL for the OHIF Viewer
 * @param urlModeParam Specific mode applicable for ohif-viewer V3 app only.
 * @returns The URL to launch the OHIF Viewer
 */
function getWebDicomUrl(data: OhifReactLaunchConfig, baseurl: string, urlModeParam = 'viewer'): string {
  const params = [];

  if (data.studyInstanceUid) {
    baseurl = `${baseurl}/${urlModeParam}/${data.studyInstanceUid}`;
  } else if (data.sessionId) {
    baseurl = `${baseurl}/${urlModeParam}`;
    params.push(`sessionId=${data.sessionId}`);
  }
  if (data.acquisitionIds) {
    params.push(`acquisitionIds=${data.acquisitionIds.join()}`);
  }
  if (data.fileIds) {
    params.push(`fileIds=${data.fileIds.join()}`);
    
    if (data.analysis){
       params.push(`analysis=${data.analysis._id}`);
    }
  }

  if (params.length > 0) {
    baseurl += `?${params.join('&')}`;
  }

  return baseurl;
}
